import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const TEXT_FIELD_NAMES = new Set(['value', 'text', 'input', 'data', 'message', 'prompt']);

const STORY_VERBS = {
  story_session_start: 'initialized',
  story_session_end: 'terminated',
  story_stage_enter: 'experienced',
  story_module_open: 'launched',
  story_module_loaded: 'initialized',
  story_module_complete: 'completed',
  story_scene_complete: 'completed',
  story_session_complete: 'completed'
};

const toSnakeKey = (key) => String(key).replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);

const normalizeDetails = (details = {}) => (
  Object.entries(details || {}).reduce((safe, [key, value]) => {
    if (TEXT_FIELD_NAMES.has(String(key).toLowerCase())) {
      return safe;
    }
    safe[toSnakeKey(key)] = value;
    return safe;
  }, {})
);

export const buildStoryEventPayload = ({
  storyId,
  moduleId,
  eventName,
  routeStep,
  details = {}
}) => {
  const safeDetails = normalizeDetails(details);
  const context = {
    source: 'story_player',
    story_id: storyId,
    story_module_id: moduleId,
    route_step: routeStep
  };

  if (safeDetails.stage) context.stage = safeDetails.stage;
  if (safeDetails.scene_id) context.scene_id = safeDetails.scene_id;
  if (safeDetails.target_module_id) context.target_module_id = safeDetails.target_module_id;
  if (safeDetails.sequence_id) context.sequence_id = safeDetails.sequence_id;
  if (safeDetails.sequence_index !== undefined && safeDetails.sequence_index !== null) {
    context.sequence_index = safeDetails.sequence_index;
  }

  return {
    event_name: eventName,
    verb: safeDetails.verb || STORY_VERBS[eventName] || 'experienced',
    object_id: safeDetails.object_id || `stories/${storyId}/${eventName}`,
    object_name: safeDetails.object_name || eventName,
    phase: safeDetails.phase || safeDetails.stage || 'story',
    status: safeDetails.status,
    success: safeDetails.success,
    completed: safeDetails.completed,
    duration_ms: safeDetails.duration_ms,
    result: safeDetails.result,
    context,
    extensions: {
      story: {
        story_id: storyId,
        stage: safeDetails.stage,
        scene_id: safeDetails.scene_id,
        target_module_id: safeDetails.target_module_id,
        sequence_id: safeDetails.sequence_id,
        sequence_index: safeDetails.sequence_index
      }
    }
  };
};

export const resolveStoryConsent = async () => {
  const token = localStorage.getItem('access_token');

  if (!token) {
    const anonymousConsent = localStorage.getItem('anonymousConsent');
    if (!anonymousConsent) return false;
    if (anonymousConsent === 'true') return true;

    try {
      const parsedConsent = JSON.parse(anonymousConsent);
      return !!parsedConsent?.data_collection_accepted;
    } catch (error) {
      return false;
    }
  }

  try {
    const response = await axios.get('/api/auth/consent/check', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return !!response.data?.has_consent;
  } catch (error) {
    console.error('[StoryTelemetry] Failed to check consent:', error);
    return false;
  }
};

export class StoryTelemetryClient {
  constructor({ storyId, moduleId, now = () => Date.now() }) {
    this.storyId = storyId;
    this.moduleId = moduleId;
    this.now = now;
    this.sessionData = null;
    this.routeStep = 0;
    this.startedAt = null;
    this.pendingEvents = [];
  }

  getHeaders(includeJson = true) {
    const token = localStorage.getItem('access_token');
    const headers = includeJson ? { 'Content-Type': 'application/json' } : {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  async start() {
    if (this.sessionData?.session_id) {
      return this.sessionData;
    }

    const response = await axios.post(
      '/api/telemetry/session/start',
      { module_id: this.moduleId },
      { headers: this.getHeaders(true) }
    );

    this.sessionData = response.data;
    this.startedAt = this.now();
    await this.log('story_session_start', {
      stage: 'story',
      sceneId: 'story',
      status: 'started'
    });
    await this.flushPendingEvents();
    return this.sessionData;
  }

  buildEvent(eventName, details = {}) {
    this.routeStep += 1;
    const payload = buildStoryEventPayload({
      storyId: this.storyId,
      moduleId: this.moduleId,
      eventName,
      routeStep: this.routeStep,
      details
    });

    return {
      session_id: this.sessionData.session_id,
      user_id: this.sessionData.user_id,
      guest_id: this.sessionData.guest_id,
      module_id: this.moduleId,
      event_type: 'game_event',
      payload,
      timestamp: new Date().toISOString(),
      client_timestamp: this.now()
    };
  }

  async upload(events) {
    if (!events.length) return;

    const response = await fetch('/api/telemetry/events', {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({
        session_id: this.sessionData.session_id,
        events
      })
    });

    if (!response.ok) {
      throw new Error(`Story telemetry upload failed: ${response.status}`);
    }
  }

  async log(eventName, details = {}) {
    if (!this.sessionData?.session_id) {
      this.pendingEvents.push({ eventName, details });
      return null;
    }

    const event = this.buildEvent(eventName, details);
    await this.upload([event]);
    return event;
  }

  async flushPendingEvents() {
    if (!this.pendingEvents.length || !this.sessionData?.session_id) return;
    const pending = [...this.pendingEvents];
    this.pendingEvents = [];
    const events = pending.map(({ eventName, details }) => this.buildEvent(eventName, details));
    await this.upload(events);
  }

  async end(details = {}) {
    if (!this.sessionData?.session_id) return;
    const sessionId = this.sessionData.session_id;

    try {
      await this.log('story_session_end', {
        stage: 'story',
        sceneId: 'story',
        status: 'ended',
        durationMs: this.startedAt ? this.now() - this.startedAt : undefined,
        ...details
      });
    } catch (error) {
      // Route changes should not be blocked by telemetry cleanup.
    }

    try {
      await axios.post('/api/telemetry/session/end', null, {
        params: { session_id: sessionId },
        headers: this.getHeaders(false)
      });
    } finally {
      this.sessionData = null;
    }
  }
}

export const useStoryTelemetry = ({ storyId, moduleId }) => {
  const clientRef = useRef(null);
  const [isStoryTelemetryReady, setIsStoryTelemetryReady] = useState(false);

  useEffect(() => {
    const client = new StoryTelemetryClient({ storyId, moduleId });
    let cancelled = false;
    clientRef.current = client;

    resolveStoryConsent()
      .then((hasConsent) => {
        if (cancelled) {
          clientRef.current = null;
          return null;
        }
        if (!hasConsent) {
          clientRef.current = null;
          return null;
        }
        return client.start().then((sessionData) => {
          if (cancelled) {
            client.end().catch((error) => {
              console.error('[StoryTelemetry] Failed to end late story session:', error);
            });
            return null;
          }
          return sessionData;
        });
      })
      .then((sessionData) => {
        if (!sessionData) return;
        if (!cancelled) {
          setIsStoryTelemetryReady(true);
        }
      })
      .catch((error) => {
        console.error('[StoryTelemetry] Failed to start story session:', error);
      });

    return () => {
      cancelled = true;
      setIsStoryTelemetryReady(false);
      const activeClient = clientRef.current;
      clientRef.current = null;
      if (activeClient?.sessionData?.session_id) {
        activeClient.end().catch((error) => {
          console.error('[StoryTelemetry] Failed to end story session:', error);
        });
      }
    };
  }, [moduleId, storyId]);

  const logStoryEvent = useCallback((eventName, details = {}) => {
    const client = clientRef.current;
    if (!client) return Promise.resolve(null);
    return client.log(eventName, details).catch((error) => {
      console.error('[StoryTelemetry] Failed to log story event:', error);
      return null;
    });
  }, []);

  return {
    logStoryEvent,
    isStoryTelemetryReady
  };
};
